import { TeamMember } from "../admin/TeamTypes";
import { User } from "../admin/UserTypes";
import { Patient } from "../healthcare/PatientTypes";

export type consultStatus = "pending" | "started" | "paused" | "ended";
export type consultParticipantRoles = "Patient" | "Lead Practitioner" | "Friend" | "Family Member" | "Carer" | "Practitioner" | "Nurse" | "Consultant";


export interface consultParticipant {
	speakerTag: string | null,
	participant: TeamMember | Patient | User |string,
	role: consultParticipantRoles
}

export interface VideoParticipant {
	socketId: string;
	participantType: string;
	userId: string;
	ready: boolean;
	videoEnabled: boolean;
	audioEnabled: boolean;
	joinedAt?: number;
	/** Human-readable label shown on the video tile (e.g. "Dr. Smith", "Patient") */
	displayName?: string;
	/** Unique stable ID used as the participantId when sending audio for transcription.
	 *  Format: `{participantType}_{userId}` – allows speaker matching across reconnects.
	 *  The patient's `userId` half is a per-room UUID persisted in `sessionStorage`
	 *  (`utils/consult/participantIdentity`), so a mid-consult refresh keeps its speaker. */
	audioParticipantId?: string;
}

/** `participantId` (as tagged on every audio chunk) → the name a human reads for that speaker.
 *  Built by `utils/consult/participantIdentity`; anything missing falls back to the prettified id. */
export type ParticipantDisplayNames = Record<string, string>;

// ─── Speaker naming (S25) ─────────────────────────────────────────────────────────────────

export type SpeakerRole = "practitioner" | "patient" | "other";

/**
 * One speaker-label → person binding, in exactly the shape talk stores and broadcasts
 * (`talk/server.js`, `assignSpeaker` / `speakerMapUpdated`).
 */
export interface SpeakerAssignment {
	/** The microphone this speaker is known to be on, when one is known — usually `null` in person. */
	participantId: string | null;
	displayName: string;
	role: SpeakerRole;
	/** `manual` today. `enrollment` is the seam: voice enrollment writes this same shape. */
	source: "manual" | "enrollment";
	/** Server clock, epoch ms. Absent on a local optimistic entry until the broadcast lands. */
	updatedAt?: number;
}

/** Speaker label (a diarisation label in person, the `participantId` online) → who that is. */
export type SpeakerMap = Record<string, SpeakerAssignment>;

/** What the assignment UI hands to `assignSpeaker`. `null` instead of one clears the label. */
export type SpeakerParticipantInput = Pick<SpeakerAssignment, "participantId" | "displayName" | "role">;

/**
 * One transcript segment.
 *
 * Everything below `type` is optional and stays optional: a stored consult from before the
 * pipeline upgrade, and an older `transcribe` still in a clinic, both produce rows with none of
 * them. The renderer must never require one.
 */
export interface Transcript {
	id: string;
	roomId: string;
	participantId: string;
	transcript: string;
	/** Publish time in epoch ms — when inference FINISHED, not when the words were said. */
	timestamp: number;
	type?: string;
	/** ⚠️ Read by the old merge heuristic, written by no producer. Retained only so stored rows
	 *  carrying it still type-check; do not build on it. */
	serverProcessedId?: string;

	// ─── contract v2 (transcribe `app/redis_processor.py::publish_transcript`) ──────────────
	/** Who the pipeline thinks spoke: a diarisation label in-person, `participantId` online. */
	speakerLabel?: string;
	/** Capture time of the packet this segment came from, in epoch ms. */
	audioTimestamp?: number;
	/** `audioTimestamp` + the segment's offset inside the packet — when it was SAID. The
	 *  transcript feed orders on this and falls back to `timestamp`. */
	absoluteStart?: number;
	absoluteEnd?: number;
	/** The transcription's confidence, 0–1. Nothing else blended in. */
	confidence?: number;
	/** How sure the speaker matcher is of `speakerLabel`, 0–1. Published separately. */
	speakerConfidence?: number;
	/** Seconds inside the uploaded recording, final pass only — what a player seeks on. */
	start?: number;
	end?: number;
}

export interface ConsultParticularsData {
	vitals: {
		height_cm: number | null;
		weight_kg: number | null;
		bmi: number | null;
		temperature_c: number | null;
		heart_rate: number | null;
		bp_systolic: number | null;
		bp_diastolic: number | null;
		respiratory_rate: number | null;
		o2_saturation: number | null;
		pain: number | null;
	};
	physicalExam: {
		general: string;
		skin: string;
		heent: string;
		neck: string;
		respiratory: string;
		cardiovascular: string;
		gastrointestinal: string;
		genitourinary: string;
		musculoskeletal: string;
		neurological: string;
		psychiatric: string;
		endocrine: string;
		hematologic: string;
		allergic: string;
		immunologic: string;
	};
	presentCondition: {
		chiefComplaint: string;
		historyOfPresentIllness: string;
		onsetDate: Date | null;
		symptoms: string;
		aggravatingFactors: string;
		relievingFactors: string;
		associatedSymptoms: string;
		previousTreatments: string;
		lastEpisode: string;
	};
	assessment: {
		differentialDiagnosis: string;
		primaryDiagnosis: string;
		secondaryDiagnosis: string;
		planOfCare: string;
		followUpPlan: string;
		referrals: string;
	};
}

export const defaultParticularsData: ConsultParticularsData = {
	vitals: {
		height_cm: null,
		weight_kg: null,
		bmi: null,
		temperature_c: null,
		heart_rate: null,
		bp_systolic: null,
		bp_diastolic: null,
		respiratory_rate: null,
		o2_saturation: null,
		pain: null,
	},
	physicalExam: {
		general: "",
		skin: "",
		heent: "",
		neck: "",
		respiratory: "",
		cardiovascular: "",
		gastrointestinal: "",
		genitourinary: "",
		musculoskeletal: "",
		neurological: "",
		psychiatric: "",
		endocrine: "",
		hematologic: "",
		allergic: "",
		immunologic: "",
	},
	presentCondition: {
		chiefComplaint: "",
		historyOfPresentIllness: "",
		onsetDate: null,
		symptoms: "",
		aggravatingFactors: "",
		relievingFactors: "",
		associatedSymptoms: "",
		previousTreatments: "",
		lastEpisode: "",
	},
	assessment: {
		differentialDiagnosis: "",
		primaryDiagnosis: "",
		secondaryDiagnosis: "",
		planOfCare: "",
		followUpPlan: "",
		referrals: "",
	},
};